import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from "../utils/auditClient";

const NewAttributeQA = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const progressAttributeId = queryParams.get('progressAttributeId');
  const academyId = queryParams.get('academyId');
  const token = localStorage.getItem('auth-token');

  const [heading, setHeading] = useState('');
  const [subHeading, setSubHeading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const baselineRef = useRef(null);

  const buildSubmitPayload = (q = heading, a = subHeading) => {
    return {
      id: academyId || null,
      question: q || '',
      answer: a || '',
    };
  };

  useEffect(() => {
    const fetchQAData = async () => {
      if (!academyId) {
        baselineRef.current = buildSubmitPayload('', '');
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ADMIN_EDIT_PROGRESS_ATTRIBUTE_QA}?progressAttributeQAId=${academyId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.sportProgressAQAEditForm) {
            const qaData = data.data.sportProgressAQAEditForm;
            setHeading(qaData.question || '');
            setSubHeading(qaData.answer || '');
            baselineRef.current = buildSubmitPayload(
              qaData.question || '',
              qaData.answer || ''
            );
          }
        } else {
          setError('Failed to fetch QA details.');
        }
      } catch (error) {
        setError('Error fetching QA details.');
      }
    };

    fetchQAData();
  }, [academyId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!heading || !subHeading) {
      setError('Both Question and Answer fields are required.');
      return;
    }

    setError('');
    setSuccess('');

    const newPayload = buildSubmitPayload(heading, subHeading);
    const oldPayload = baselineRef.current || {};

    if (academyId) {
      const { digest, html } = buildDiffPreview(oldPayload, newPayload);
      if (!digest.length) {
        Swal.fire({
          icon: "info",
          title: "Nothing to update",
          text: "No differences detected.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: "Apply these changes?",
        html,
        showCancelButton: true,
        confirmButtonText: "Update",
      });
      if (!confirm.isConfirmed) return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ADMIN_NEW_PROGRESS_ATTRIBUTE_QA}?progressAttributeId=${progressAttributeId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(newPayload),
        }
      );

      if (response.ok) {
        fireAudit({
          entity: "progressAttributeQA",
          affected_id: academyId || null,
          action: academyId ? "update" : "create",
          businessApi: `${process.env.REACT_APP_API_ADMIN_NEW_PROGRESS_ATTRIBUTE_QA}?progressAttributeId=${progressAttributeId}`,
          businessMethod: "POST",
          oldPayload,
          newPayload,
          section: "SPORTS",
          subSection: "NewAttributeQA",
          extra: { page: "NewAttributeQA" },
        });

        setSuccess('QA submitted successfully!');
        baselineRef.current = newPayload;

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: academyId ? "QA Updated Successfully!" : "QA Added Successfully!",
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });

        navigate(-1);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to submit QA.');
      }
    } catch (error) {
      setError('An error occurred while submitting QA.');
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/sportList" className="breadcrumb-link">Sport List</Link> &gt; Create QA
      </div>
      <form onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '98%'}}>
          <input
            type="text"
            name="heading"
            className={`walkin-form ${heading ? 'has-value' : ''}`}
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            placeholder="Enter Question"
          />
          {heading && (
            <label htmlFor="heading">Question</label>
          )}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            type="text"
            name="subHeading"
            className={`walkin-form-comments ${subHeading ? 'has-value' : ''}`}
            value={subHeading}
            onChange={(e) => setSubHeading(e.target.value)}
            placeholder="Enter Answer"
            rows="5"
          />
          {subHeading && (
            <label htmlFor="subHeading">Answer</label>
          )}
        </div>

        <button className='submit-button' type="submit">
          {academyId ? "Update QA" : "Create QA"}
        </button>
      </form>
    </div>
  );
};

export default NewAttributeQA;
